import React from 'react';
import { Link } from 'react-router-dom';
import {
  Globe, Wifi, Monitor, Volume2, BarChart2, Scan, MapPin,
} from 'lucide-react';
import './Home.css';
import heroImg from '../../assets/hero-background.jpg';

export default function Home() {
  return (
    <div className="h-home">
      <Hero />
      <Problem />
      <Pillars />
      <AiCampaign />
      <Operations />
      <Measurement />
      <Tourism />
      <UseCases />
      <ComparisonTable />
      <PricingPreview />
      <FinalCta />
    </div>
  );
}

/* ================================================================
   HERO
   ================================================================ */
function Hero() {
  const channels = ['Website', 'Guest Wi-Fi', 'QR Codes', 'TV Screens', 'Audio', 'Analytics'];

  return (
    <section
      className="h-hero"
      style={{ backgroundImage: `url(${heroImg})` }}
      aria-label="Suitestream hero"
    >
      <div className="h-hero__overlay" />
      <div className="h-container">
        <p className="h-eyebrow">Venue Operations &amp; Marketing Platform</p>
        <h1>
          Turn every customer touchpoint<br />
          into one connected advertising platform.
        </h1>
        <p className="h-hero__sub">
          Suitestream connects your website, guest Wi-Fi, QR codes, TV screens, audio
          announcements, and landing pages into one system. Launch a campaign once, publish it
          everywhere, and measure what brings people through the door.
        </p>
        <div className="h-ctas">
          <Link to="/signup" className="h-btn h-btn--primary">Start Free</Link>
          <Link to="/subscription" className="h-btn h-btn--ghost">View Pricing</Link>
          <Link to="/contact" className="h-btn h-btn--ghost">Book a Demo</Link>
        </div>
        <div className="h-channel-strip" aria-hidden="true">
          {channels.map(ch => (
            <span key={ch} className="h-channel-strip__item">{ch}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   PROBLEM
   ================================================================ */
const BEFORE = [
  'Website updated weeks ago',
  'QR code still links to an old menu',
  'TVs run on a completely different schedule',
  'Wi-Fi captures traffic but feeds nothing',
  'Every promo rebuilt separately per channel',
];
const AFTER = [
  'One place to manage every channel',
  'One campaign brief generates everything',
  'Screens, QR, Wi-Fi, and website stay in sync',
  'Wi-Fi data feeds your marketing stack',
  'One report across all touchpoints',
];

function Problem() {
  return (
    <section className="h-section">
      <div className="h-container">
        <p className="h-eyebrow">The Problem</p>
        <h2 className="h-section__title" style={{ maxWidth: 620 }}>
          Most venues already have the pieces.<br />They just don't work together.
        </h2>
        <p className="h-section__body">
          Your website says one thing. Your QR code opens something else. Your TVs run on a
          different schedule. Guest Wi-Fi captures traffic but rarely feeds your real marketing
          stack. Promotions get rebuilt for social media, screens, and landing pages over and over.
          Suitestream turns those disconnected touchpoints into one coordinated system.
        </p>
        <div className="h-problem">
          <div className="h-problem__before">
            <div className="h-problem__label">Before Suitestream</div>
            <ul className="h-problem__items">
              {BEFORE.map(item => (
                <li key={item} className="h-problem__item">{item}</li>
              ))}
            </ul>
          </div>
          <div className="h-problem__after">
            <div className="h-problem__label">With Suitestream</div>
            <ul className="h-problem__items">
              {AFTER.map(item => (
                <li key={item} className="h-problem__item">{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   PRODUCT PILLARS
   ================================================================ */
const PILLARS = [
  {
    Icon: Globe,
    title: 'Website & Landing Pages',
    body: 'Launch a venue website that reflects what is happening in the real venue right now — not what was true six months ago. Campaign landing pages included.',
  },
  {
    Icon: Wifi,
    title: 'Guest Wi-Fi',
    body: 'Turn a utility into a branded entry point. Multilingual captive portal flows, customer capture, and operational insight from every connection.',
  },
  {
    Icon: Scan,
    title: 'QR Codes',
    body: 'Deploy QR menus, product pages, service pages, campaign pages, and location-aware info plaques — all from the same system.',
  },
  {
    Icon: Monitor,
    title: 'TV & Signage',
    body: 'Push promotions, brand content, event teasers, and on-screen calls to action across TVs and digital displays throughout your venue.',
  },
  {
    Icon: Volume2,
    title: 'Audio Announcements',
    body: 'Generate radio-style announcements for the house sound system to support promotions, events, and venue messaging automatically.',
  },
  {
    Icon: BarChart2,
    title: 'Audience Insight',
    body: 'Connect scans, visits, sessions, and repeat presence into a clearer picture of who engaged and who came back.',
  },
];

function Pillars() {
  return (
    <section className="h-section h-section--alt">
      <div className="h-container">
        <p className="h-eyebrow">The Platform</p>
        <h2 className="h-section__title h-section__title--center">
          One platform. Six revenue and customer-experience surfaces.
        </h2>
        <div className="h-grid h-grid--3" style={{ marginTop: 40 }}>
          {PILLARS.map(({ Icon, title, body }) => (
            <div key={title} className="h-card h-card--orange-border">
              <div className="h-card__icon">
                <Icon size={22} strokeWidth={1.8} />
              </div>
              <h3 className="h-card__title">{title}</h3>
              <p className="h-card__body">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   AI CAMPAIGN CREATION
   ================================================================ */
const AI_OUTPUTS = [
  'Campaign Artwork',
  'Landing Page Copy',
  'Social Post Drafts',
  'TV Screen Content',
  'QR Destination Page',
  'Audio Script',
];

function AiCampaign() {
  return (
    <section className="h-section">
      <div className="h-container">
        <p className="h-eyebrow">AI Campaign Creation</p>
        <h2 className="h-section__title h-section__title--center">
          Describe the promotion once.<br />Suitestream builds the campaign.
        </h2>
        <p className="h-section__body h-section__body--center">
          Venue managers should not need a designer, copywriter, media buyer, and AV technician
          just to push tonight's offer. One prompt generates everything.
        </p>
        <div className="h-terminal" role="presentation">
          <div className="h-terminal__bar" aria-hidden="true">
            <div className="h-terminal__dot" />
            <div className="h-terminal__dot" />
            <div className="h-terminal__dot" />
          </div>
          <div className="h-terminal__label">Campaign Prompt</div>
          <p className="h-terminal__prompt">
            "Friday sunset cocktails, 2-for-1 from 5–7pm, live DJ from 8pm — push to TVs,
            QR, Wi-Fi, and socials."
          </p>
          <hr className="h-terminal__divider" />
          <div className="h-terminal__label">Generated Output</div>
          <div className="h-terminal__outputs">
            {AI_OUTPUTS.map(o => (
              <div key={o} className="h-output-chip">{o}</div>
            ))}
          </div>
          <p className="h-terminal__tagline">
            One prompt. Multiple channels. Consistent campaign.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   VENUE OPERATIONS
   ================================================================ */
const DEVICES = [
  { name: 'Bar — Screen A', meta: 'Happy Hour Promo', on: true },
  { name: 'Bar — Screen B', meta: 'Sports Feed', on: true },
  { name: 'Terrace — Display', meta: 'Scheduled: 17:00', on: false },
  { name: 'Entrance — Screen', meta: 'Welcome Loop', on: true },
  { name: 'Private Room', meta: 'Offline', on: false },
];

function Operations() {
  return (
    <section className="h-section h-section--petrol">
      <div className="h-container">
        <p className="h-eyebrow">Venue Operations</p>
        <div className="h-ops-layout">
          <div>
            <h2 className="h-section__title">
              The screens turn on.<br />The right content plays.<br />The venue opens cleaner.
            </h2>
            <p className="h-section__body">
              Suitestream runs on any device that can open a browser. For operators who want
              deeper control, the platform adds another layer.
            </p>
            <ul className="h-ops-list">
              <li>Display overlays, QR codes, and system messages on top of any screen content</li>
              <li>Launch and recover apps on any connected device remotely</li>
              <li>Monitor device and playback health across your whole venue</li>
              <li>Detect stalled playback and trigger automatic recovery</li>
              <li>Schedule power on and off times across every screen</li>
            </ul>
            <div className="h-ops-callout">
              From one-screen venues to multi-room sites — reduces the manual setup and daily
              friction that costs your staff time every morning.
            </div>
          </div>
          <div className="h-ops-visual" aria-hidden="true">
            <div className="h-ops-visual__header">Device Status</div>
            {DEVICES.map(d => (
              <div key={d.name} className="h-device-row">
                <div className={`h-device-row__dot h-device-row__dot--${d.on ? 'on' : 'off'}`} />
                <div className="h-device-row__name">{d.name}</div>
                <div className="h-device-row__meta">{d.meta}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   MEASUREMENT
   ================================================================ */
const STATS = [
  { value: 'Wi-Fi', label: 'Session & visit visibility' },
  { value: 'QR', label: 'Scan & engagement tracking' },
  { value: 'LP', label: 'Campaign landing activity' },
  { value: 'Pixel', label: 'GA4, Meta, Clarity & TikTok on higher plans' },
];

const MEASUREMENT_PILLS = [
  'Session and visit visibility',
  'Repeat-visitor signals',
  'Campaign landing activity',
  'QR engagement data',
  'Custom analytics integrations',
];

function Measurement() {
  return (
    <section className="h-section h-section--alt">
      <div className="h-container">
        <p className="h-eyebrow">Measurement</p>
        <h2 className="h-section__title" style={{ maxWidth: 560 }}>
          Stop guessing whether the promotion worked.
        </h2>
        <p className="h-section__body" style={{ maxWidth: 620 }}>
          Suitestream connects campaign touchpoints to visitor activity across Wi-Fi sessions,
          QR interactions, and landing-page visits — so operators can get closer to answering
          the question that matters:{' '}
          <em style={{ color: 'var(--ink)', fontStyle: 'italic' }}>
            how many people who saw the ad actually came to the venue?
          </em>
        </p>
        <div className="h-stats-grid">
          {STATS.map(s => (
            <div key={s.value} className="h-stat">
              <div className="h-stat__value">{s.value}</div>
              <div className="h-stat__label">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="h-pill-row">
          {MEASUREMENT_PILLS.map(p => (
            <span key={p} className="h-pill">{p}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   TOURISM VERTICAL
   ================================================================ */
const TOURISM_SITES = [
  { name: 'Linh Ung Pagoda', desc: 'QR plaques, multilingual info, route maps' },
  { name: 'Hue Imperial Citadel', desc: 'Guided exploration, language switching' },
  { name: 'Museum Campuses', desc: 'Exhibit information updated without reprinting' },
  { name: 'Heritage Walking Routes', desc: 'Linked points of interest across a large site' },
];

function Tourism() {
  return (
    <section className="h-section h-tourism">
      <div className="h-container">
        <p className="h-eyebrow">Second Vertical</p>
        <div className="h-tourism-layout">
          <div>
            <h2 className="h-section__title">
              Not just for venues.<br />Also built for places people come to learn.
            </h2>
            <p className="h-section__body">
              Suitestream also works for monuments, museums, pagodas, citadels, sculpture parks,
              and major visitor attractions. With permission from site owners, QR plaques can be
              placed near statues, landmarks, and exhibits to unlock a richer visitor experience.
            </p>
            <ul className="h-tourism-features">
              <li>Multilingual information triggered by QR scan</li>
              <li>Maps and orientation for large or complex sites</li>
              <li>Linked routes across multiple points of interest</li>
              <li>Content updated instantly — no reprinting required</li>
              <li>Campaign or partner content where appropriate</li>
            </ul>
          </div>
          <div className="h-tourism-sites">
            {TOURISM_SITES.map(s => (
              <div key={s.name} className="h-tourism-site">
                <span className="h-tourism-site__icon">
                  <MapPin size={16} strokeWidth={1.8} />
                </span>
                <div>
                  <div className="h-tourism-site__name">{s.name}</div>
                  <div className="h-tourism-site__desc">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   USE CASES
   ================================================================ */
const USE_CASES = [
  {
    title: 'Restaurants & Cafes',
    body: 'Menu QR, loyalty capture, lunch promos, review prompts, and daypart campaigns.',
  },
  {
    title: 'Bars & Beach Clubs',
    body: 'DJ promos, event screens, guest Wi-Fi journeys, artist pages, and audio announcements.',
  },
  {
    title: 'Hotels & Resorts',
    body: 'Arrival messaging, local activity upsell, in-room screen promotion, and multilingual visitor information.',
  },
  {
    title: 'Retail & Service Venues',
    body: 'Product QR, digital signage, capture pages, and retargeting-ready traffic insight.',
  },
  {
    title: 'Tourist Attractions',
    body: 'Plaques, maps, routes, language switching, and content layers that bring a site to life.',
  },
  {
    title: 'Multi-Site Groups',
    body: 'Roll out campaigns across locations while keeping local control where it is needed.',
  },
];

function UseCases() {
  return (
    <section className="h-section">
      <div className="h-container">
        <p className="h-eyebrow">Use Cases</p>
        <h2 className="h-section__title h-section__title--center">
          Built for the places people actually spend time.
        </h2>
        <div className="h-grid h-grid--3" style={{ marginTop: 40 }}>
          {USE_CASES.map(u => (
            <div key={u.title} className="h-card">
              <div className="h-usecase">
                <h3 className="h-usecase__title">{u.title}</h3>
                <p className="h-usecase__body">{u.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   COMPARISON TABLE
   ================================================================ */
const COMPARE_COLS = [
  'Website & Pages', 'Guest Wi-Fi', 'QR / Plaques', 'TV Signage', 'Audio', 'Device Control', 'Tourism',
];

const COMPARE_ROWS = [
  {
    name: 'Suitestream',
    vals: ['strong', 'strong', 'strong', 'strong', 'strong', 'strong', 'strong'],
  },
  {
    name: 'Wi-Fi platforms (Purple, Beambox)',
    vals: ['partial', 'strong', 'partial', 'weak', 'weak', 'weak', 'weak'],
  },
  {
    name: 'Signage platforms (Raydiant, Yodeck)',
    vals: ['weak', 'weak', 'partial', 'strong', 'partial', 'partial', 'weak'],
  },
  {
    name: 'Audio-tour platforms (izi.TRAVEL)',
    vals: ['partial', 'weak', 'strong', 'weak', 'strong', 'weak', 'strong'],
  },
];

function CompCell({ val }) {
  if (val === 'strong')  return <span className="h-compare__check">✓</span>;
  if (val === 'partial') return <span className="h-compare__partial">Partial</span>;
  return <span className="h-compare__weak">—</span>;
}

function ComparisonTable() {
  return (
    <section className="h-section h-section--alt">
      <div className="h-container">
        <p className="h-eyebrow">Competitive Landscape</p>
        <h2 className="h-section__title" style={{ maxWidth: 580 }}>
          Others solve one surface.<br />Suitestream connects the whole venue journey.
        </h2>
        <p className="h-section__body">
          Most venue tech tools solve one problem: signage, or Wi-Fi, or QR menus.
          Suitestream is the only platform that spans all of them — and ties them to a
          single campaign workflow.
        </p>
        <div className="h-compare-wrap">
          <table className="h-compare">
            <thead>
              <tr>
                <th>Platform</th>
                {COMPARE_COLS.map(c => <th key={c}>{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map(row => (
                <tr key={row.name}>
                  <td>{row.name}</td>
                  {row.vals.map((v, i) => (
                    <td key={i}><CompCell val={v} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

const PRICING_PREVIEW_PACKAGES = [
  {
    title: 'Free',
    body: 'Ad-supported single-site starter. Hosted website, captive Wi-Fi, QRs, and up to 5 TVs included.',
  },
  {
    title: 'Basic',
    body: 'Low-cost ad-supported package for budget operators and multi-site accounts that still want to keep prices down.',
  },
  {
    title: 'Standard',
    body: 'Ad-free paid default for most venues, including own DNS, promotions, and a cleaner branded website experience.',
  },
  {
    title: 'Premium / Enterprise',
    body: 'High-capacity rollout for screen-heavy venues and negotiated group deals for larger operators.',
  },
];

function PricingPreview() {
  return (
    <section className="h-section">
      <div className="h-container">
        <p className="h-eyebrow">Pricing</p>
        <h2 className="h-section__title h-section__title--center">
          Start with one site. Scale package-by-package.
        </h2>
        <p className="h-section__body h-section__body--center">
          Suitestream pricing is site-based, so one budget bar can stay ad-supported while another venue in the same account runs on Premium. See the full package matrix in VND.
        </p>
        <div className="h-grid h-grid--2" style={{ marginTop: 36 }}>
          {PRICING_PREVIEW_PACKAGES.map((entry) => (
            <div key={entry.title} className="h-card h-card--orange-border">
              <h3 className="h-card__title">{entry.title}</h3>
              <p className="h-card__body">{entry.body}</p>
            </div>
          ))}
        </div>
        <div className="h-ctas" style={{ justifyContent: 'center', marginTop: 28 }}>
          <Link to="/subscription" className="h-btn h-btn--primary">View Pricing</Link>
          <Link to="/contact" className="h-btn h-btn--ghost">Talk to Sales</Link>
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   FINAL CTA
   ================================================================ */
function FinalCta() {
  return (
    <section className="h-cta-band">
      <div className="h-container">
        <h2>
          If your venue already has a website, TVs, QR codes, and guest Wi-Fi —
          you're already halfway there.
        </h2>
        <p>
          Suitestream ties them together into one system your staff can actually run.
        </p>
        <div className="h-ctas" style={{ justifyContent: 'center' }}>
          <Link to="/signup" className="h-btn h-btn--primary">Start Free</Link>
          <Link to="/subscription" className="h-btn h-btn--ghost">View Pricing</Link>
          <Link to="/contact" className="h-btn h-btn--ghost">Book a Demo</Link>
        </div>
      </div>
    </section>
  );
}
